'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import Navbar from '../components/Navbar'
import { authenticatedFetch } from '@/lib/client-fetch'

export default function ForgotPasswordPage() {
  const router = useRouter()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess(false)
    setLoading(true)

    if (!email || !email.trim()) {
      setError('Please enter your email address')
      setLoading(false)
      return
    }

    try {
      const response = await authenticatedFetch('/api/auth/password-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send password reset email')
      }

      setSuccess(true)
    } catch (err: any) {
      setError(err.message || 'Failed to send password reset email. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />

      <div className="max-w-md mx-auto px-8 py-12">
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-serif font-semibold tracking-tight text-ink mb-2">Forgot Password</h1>
          <p className="text-ink-soft">Enter your email address and we'll send you a link to reset your password</p>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg shadow-ink/[0.06] border border-cream-deep">
          {success ? (
            <div className="text-center space-y-4">
              <div className="bg-green-50 border border-green-300 text-green-700 px-4 py-3 rounded-lg">
                <p className="font-semibold mb-1">Email Sent!</p>
                <p className="text-sm">
                  If an account exists with this email, a password reset link has been sent. 
                  Please check your inbox and click the link to reset your password.
                </p>
                <p className="text-sm mt-2">
                  The link will expire in 30 minutes.
                </p>
              </div>
              <Link
                href="/login"
                className="block w-full bg-ember text-white py-3 rounded-full font-semibold hover:bg-ember-hover transition-colors shadow-md shadow-ember/25 text-center"
              >
                Back to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-gray-700 text-sm mb-2 font-medium">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full bg-white text-ink px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-ember text-white py-3 rounded-full font-semibold hover:bg-ember-hover transition-colors shadow-md shadow-ember/25 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>

              <div className="text-center">
                <Link 
                  href="/login" 
                  className="text-ember hover:text-ember-hover hover:underline font-medium text-sm"
                >
                  Back to Login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

